import React from "react";
import { FiChevronDown, FiRefreshCw, FiSliders } from "react-icons/fi";
import { Dropdown, DropdownContent } from "../../BaseUI/Dropdown";
import { translate } from "../../../hooks/translate";
import { useTheme } from "../../../hooks/useTheme";
import ColumnChipSection from "./sections/ColumnChipSection";
import RadioSection from "./sections/RadioSection";
import ChipMultiSection from "./sections/ChipMultiSection";
import MultiSelectListSection from "./sections/MultiSelectListSection";
import DateRangeSection from "./sections/DateRangeSection";
import PickerSection from "./sections/PickerSection";
import PeriodDateSection from "./sections/PeriodDateSection";
import styles from "./ViewOptionsPanel.module.scss";

const renderSection = (section) => {
  switch (section.type) {
    case "columns":
      return (
        <ColumnChipSection
          key={section.id}
          title={section.title}
          columns={section.columns}
          visibleColumns={section.visibleColumns}
          onToggle={section.onToggle}
          onReset={section.onReset}
          defaultOpen={section.defaultOpen ?? true}
        />
      );
    case "radio":
      return (
        <RadioSection
          key={section.id}
          title={section.title}
          value={section.value}
          options={section.options}
          onChange={section.onChange}
          defaultOpen={section.defaultOpen ?? true}
          searchPlaceholder={section.searchPlaceholder}
        />
      );
    case "chips":
      return (
        <ChipMultiSection
          key={section.id}
          title={section.title}
          selectedIds={section.selectedIds}
          options={section.options}
          onChange={section.onChange}
          showAllOption={section.showAllOption}
          allLabel={section.allLabel}
          defaultOpen={section.defaultOpen ?? true}
          searchPlaceholder={section.searchPlaceholder}
        />
      );
    case "multiList":
      return (
        <MultiSelectListSection
          key={section.id}
          title={section.title}
          selectedIds={section.selectedIds}
          options={section.options}
          onChange={section.onChange}
          showAllOption={section.showAllOption}
          allLabel={section.allLabel}
          defaultOpen={section.defaultOpen ?? true}
          searchPlaceholder={section.searchPlaceholder}
        />
      );
    case "dateRange":
      return (
        <DateRangeSection
          key={section.id}
          title={section.title}
          startDate={section.startDate}
          endDate={section.endDate}
          onStartChange={section.onStartChange}
          onEndChange={section.onEndChange}
          defaultOpen={section.defaultOpen ?? true}
        />
      );
    case "periodDate":
      return (
        <PeriodDateSection
          key={section.id}
          title={section.title}
          mode={section.mode}
          anchorDate={section.anchorDate}
          startDate={section.startDate}
          endDate={section.endDate}
          onChange={section.onChange}
          maxDate={section.maxDate}
          defaultOpen={section.defaultOpen ?? true}
        />
      );
    case "picker":
      return (
        <PickerSection
          key={section.id}
          title={section.title}
          mode={section.mode}
          value={section.value}
          selectedIds={section.selectedIds}
          onChange={section.onChange}
          options={section.options}
          allValue={section.allValue}
          allLabel={section.allLabel}
          showAllOption={section.showAllOption}
          searchPlaceholder={section.searchPlaceholder}
          emptyHint={section.emptyHint}
          emptyResultsLimit={section.emptyResultsLimit}
          recentStorageKey={section.recentStorageKey}
          defaultOpen={section.defaultOpen ?? true}
          loadOptions={section.loadOptions}
        />
      );
    case "custom":
      return section.render ? (
        <div key={section.id}>{section.render()}</div>
      ) : null;
    default:
      return null;
  }
};

const ViewOptionsPanel = ({
  triggerLabel = "Görünüm & Filtreler",
  activeFilterCount = 0,
  sections = [],
  onApply,
  onReset,
  showApply = true,
  showReset = true,
}) => {
  const theme = useTheme();
  const visibleSections = sections.filter(Boolean);

  return (
    <Dropdown
      className={styles.root}
      placement="bottom-end"
      border="none"
      closeOnSelect={false}
      customTrigger
      hideDropdownIcon
      maxHeight="none"
      contentClassName={styles.dropdownShell}
      trigger={
        <button
          type="button"
          className={styles.trigger}
          aria-label={triggerLabel}
        >
          <FiSliders size={14} />
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          {activeFilterCount > 0 && (
            <span className={styles.badge}>{activeFilterCount}</span>
          )}
          <FiChevronDown size={12} className={styles.chevron} />
        </button>
      }
    >
      <DropdownContent className={styles.panel}>
        <div className={styles.themeScope} m-data-theme={theme}>
          <div className={styles.body}>
            {visibleSections.map(renderSection)}
          </div>
          {(showApply || showReset) && (
            <div className={styles.footer}>
              {showReset && activeFilterCount > 0 && onReset ? (
                <button
                  type="button"
                  className={styles.footerClear}
                  onClick={onReset}
                >
                  {translate("UI.CLEAR_ALL_FILTERS") || "Filtreleri Temizle"}
                </button>
              ) : (
                <span />
              )}
              {showApply && onApply && (
                <button
                  type="button"
                  className={styles.footerApply}
                  onClick={onApply}
                >
                  <FiRefreshCw size={13} />
                  Uygula
                </button>
              )}
            </div>
          )}
        </div>
      </DropdownContent>
    </Dropdown>
  );
};

export default ViewOptionsPanel;
